import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../../lib/firebase";
import type { SmartTagAttachmentFile, VCard } from "../../../types/vcard";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const getMyTechWarrantyFiles = (
  formData: Partial<VCard>,
): SmartTagAttachmentFile[] => {
  const categorySpecificInfo = formData.categorySpecificInfo;
  if (!isRecord(categorySpecificInfo)) {
    return [];
  }

  const myTech = categorySpecificInfo.myTech;
  if (!isRecord(myTech)) {
    return [];
  }

  const attachments = myTech.attachments;
  if (!isRecord(attachments)) {
    return [];
  }

  const warranty = attachments.warranty;
  if (!isRecord(warranty) || !Array.isArray(warranty.files)) {
    return [];
  }

  return warranty.files as SmartTagAttachmentFile[];
};

export const withMyTechWarrantyFiles = (
  formData: Partial<VCard>,
  files: SmartTagAttachmentFile[],
): Partial<VCard> => {
  const categorySpecificInfo = isRecord(formData.categorySpecificInfo)
    ? formData.categorySpecificInfo
    : {};
  const myTech = isRecord(categorySpecificInfo.myTech)
    ? categorySpecificInfo.myTech
    : {};
  const attachments = isRecord(myTech.attachments) ? myTech.attachments : {};
  const warranty = isRecord(attachments.warranty) ? attachments.warranty : {};

  return {
    ...formData,
    categorySpecificInfo: {
      ...categorySpecificInfo,
      myTech: {
        ...myTech,
        attachments: {
          ...attachments,
          warranty: {
            ...warranty,
            files,
          },
        },
      },
    },
  };
};

// Persist the warranty files list directly to the active myTech vCard
// without going through the form-submit pipeline. Scoped dot-notation
// update so other unsaved local form fields are untouched. Eliminates
// the "uploaded but not saved" orphan class — uploads are immediately
// the source of truth for the user.
export const persistMyTechWarrantyFiles = async (
  userId: string,
  files: SmartTagAttachmentFile[],
): Promise<void> => {
  const vcardRef = doc(db, `users/${userId}/vcards/active`, "myTech");
  await updateDoc(vcardRef, {
    "categorySpecificInfo.myTech.attachments.warranty.files": files,
    updatedAt: serverTimestamp(),
  });
};
